"use client";

import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { alpha, styled, useTheme } from "@mui/material/styles";

import {
  AttachMoney,
  CalendarToday,
  CheckCircle,
  Close,
  Description,
  ErrorOutline,
  InfoOutlined,
  Paid,
  PersonSearch,
  ReceiptLong,
  Refresh,
  Save,
  School,
  Search,
  TrendingUp,
  Verified,
  WarningAmber,
} from "@mui/icons-material";

import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import { useBranch } from "../../../layouts/BranchContext";

/* =========================================================
   CUSTOM TABLE SCROLLBAR
========================================================= */

const CustomTableScrollbar = styled("div")(({ theme }) => ({
  overflowY: "auto",
  overflowX: "auto",
  "&::-webkit-scrollbar": {
    width: "6px",
    height: "6px",
    background: "transparent",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor:
      theme.palette.mode === "dark" ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.2)",
    borderRadius: "3px",
  },
  "&::-webkit-scrollbar-thumb:hover": {
    backgroundColor:
      theme.palette.mode === "dark" ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.3)",
  },
  scrollbarWidth: "thin",
  scrollbarColor: `${
    theme.palette.mode === "dark" ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.2)"
  } transparent`,
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  "&::-webkit-scrollbar": {
    width: "6px",
    background: "transparent",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor:
      theme.palette.mode === "dark" ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.2)",
    borderRadius: "3px",
  },
  "&::-webkit-scrollbar-thumb:hover": {
    backgroundColor:
      theme.palette.mode === "dark" ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.3)",
  },
  scrollbarWidth: "thin",
  scrollbarColor: `${
    theme.palette.mode === "dark" ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.2)"
  } transparent`,
}));

/* =========================================================
   API
========================================================= */

const API_BASE_URL = "https://wims.revisetoexcel.com/api";

const STUDENTS_PENDING_APPROVAL_API = "/admission/fully-paid-pending-certificate/";
const APPROVAL_LIST_API = "/admission/approval/list/";
const APPROVAL_SAVE_API = "/admission/approval/save/";
const DASHBOARD_COUNT_API = "/admission/certificate-approval/dashboard-count/";

const api = axios.create({
  baseURL: API_BASE_URL,
});

/* =========================================================
   ACCESS TOKEN
========================================================= */

const getAccessToken = () => {
  if (typeof window === "undefined") return null;

  const keys = ["accessToken", "access_token", "token"];

  for (const key of keys) {
    const localToken = localStorage.getItem(key);

    if (localToken) return localToken;

    const sessionToken = sessionStorage.getItem(key);

    if (sessionToken) return sessionToken;
  }

  return null;
};

api.interceptors.request.use(
  (config) => {
    const token = getAccessToken();

    if (token) {
      config.headers = config.headers || {};

      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

/* =========================================================
   USER
========================================================= */

const getStoredUser = () => {
  if (typeof window === "undefined") return null;

  try {
    const storedUser =
      sessionStorage.getItem("user") || localStorage.getItem("user");

    if (!storedUser) return null;

    return JSON.parse(storedUser);
  } catch (error) {
    console.error("User parse error:", error);

    return null;
  }
};

/* =========================================================
   ERROR / RESPONSE HELPERS
========================================================= */

const prettifyFieldLabel = (field) =>
  String(field || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const getErrorMessage = (error, fallback = "Something went wrong.") => {
  if (error?.message === "Network Error")
    return "Unable to connect to the server. Please check your internet connection.";

  if (error?.code === "ECONNABORTED")
    return "Request timed out. Please try again later.";

  const status = error?.response?.status;

  const data = error?.response?.data;

  if (status === 401) return "Your session has expired. Please log in again.";

  if (status === 403)
    return "You don't have permission to perform this action.";

  if (status === 404) return "The requested resource was not found.";

  if (typeof data === "string" && data.trim()) return data;

  if (data?.detail) return String(data.detail);

  if (data?.message) return String(data.message);

  if (data?.error) return String(data.error);

  if (data && typeof data === "object") {
    const entries = Object.entries(data).filter(
      ([, value]) => value !== null && value !== undefined && value !== ""
    );

    const first = entries[0];

    if (first) {
      const value = Array.isArray(first[1]) ? first[1][0] : first[1];

      if (typeof value === "string") {
        return `${prettifyFieldLabel(first[0])}: ${value}`;
      }
    }
  }

  if (error?.message) return error.message;

  return fallback;
};

const getArray = (response) => {
  const data = response?.data;

  if (Array.isArray(data)) return data;

  if (Array.isArray(data?.results)) return data.results;

  if (Array.isArray(data?.data)) return data.data;

  return [];
};

const formatCurrency = (amount) => {
  if (amount === null || amount === undefined || amount === "") return "—";

  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Number(amount));
};

/* =========================================================
   SECTION LABEL
========================================================= */

const SectionLabel = ({ icon: Icon, text }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2, mt: 1 }}>
    <Box sx={{ width: 3, height: 18, borderRadius: 4, bgcolor: "primary.main" }} />

    {Icon && <Icon sx={{ fontSize: 15, color: "primary.main" }} />}

    <Typography
      sx={{
        fontSize: "0.72rem",
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: 1,
        color: "primary.main",
      }}
    >
      {text}
    </Typography>
  </Box>
);

/* =========================================================
   STAT CARD
========================================================= */

const StatCard = ({ icon: Icon, label, value, color }) => (
  <Card
    sx={{
      flex: 1,
      minWidth: 130,
      borderRadius: 2,
      boxShadow: "none",
      border: "1px solid",
      borderColor: "divider",
    }}
  >
    <CardContent
      sx={{ p: 2, display: "flex", alignItems: "center", gap: 1.5, "&:last-child": { pb: 2 } }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: 1.5,
          backgroundColor: alpha(color, 0.12),
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <Icon sx={{ color, fontSize: 20 }} />
      </Box>

      <Box>
        <Typography
          sx={{
            fontSize: "0.68rem",
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: 0.5,
            lineHeight: 1,
          }}
        >
          {label}
        </Typography>

        <Typography sx={{ fontSize: "1.15rem", fontWeight: 700, mt: 0.3 }}>
          {value}
        </Typography>
      </Box>
    </CardContent>
  </Card>
);

/* =========================================================
   FORM ROW
========================================================= */

const FormRow = ({ label, required, children, helperText }) => (
  <Box sx={{ display: "flex", alignItems: "flex-start", mb: 2 }}>
    <Box
      sx={{
        width: { xs: 0, sm: 160 },
        minWidth: { sm: 160 },
        pt: "9px",
        textAlign: "right",
        pr: 2,
        fontSize: "0.82rem",
        fontWeight: 500,
        lineHeight: 1.3,
      }}
    >
      <Box sx={{ display: { xs: "none", sm: "block" } }}>
        {label}
        {required && <span style={{ color: "#e53935", marginLeft: 2 }}>*</span>}
      </Box>
    </Box>

    <Box sx={{ flex: 1 }}>
      {children}

      {helperText && (
        <Typography
          variant="caption"
          sx={{ color: "text.disabled", mt: 0.5, display: "block" }}
        >
          {helperText}
        </Typography>
      )}
    </Box>
  </Box>
);

/* =========================================================
   COMPONENT
========================================================= */

function CertificateApproval() {
  const theme = useTheme();

  const isDark = theme.palette.mode === "dark";

  /* Global branch selector — replaces the local branch
     dropdown and fetchBranches call. */

  const { selectedBranchId } = useBranch();

  /* =======================================================
     USER
  ======================================================= */

  const [organizationId, setOrganizationId] = useState("");

  useEffect(() => {
    const user = getStoredUser();

    if (!user) {
      console.warn("Logged-in user not found.");

      return;
    }

    const orgId = user.organization_id || user.organization?.id || user.organization || "";

    setOrganizationId(orgId ? String(orgId) : "");
  }, []);

  /* =======================================================
     DATA
  ======================================================= */

  const [students, setStudents] = useState([]);

  const [savedCertificates, setSavedCertificates] = useState([]);

  const [dashboardCounts, setDashboardCounts] = useState(null);

  /* =======================================================
     LOADING
  ======================================================= */

  const [loadingStudents, setLoadingStudents] = useState(false);

  const [loadingList, setLoadingList] = useState(false);

  const [initialLoading, setInitialLoading] = useState(true);

  const [saving, setSaving] = useState(false);

  /* =======================================================
     FORM
  ======================================================= */

    const todayStr = new Date().toISOString().split("T")[0];

  const emptyForm = {
  student: "",
  approval_date: todayStr,   // ← was ""
  remarks: "",
};

const [formData, setFormData] = useState(emptyForm);


  const [selectedStudent, setSelectedStudent] = useState(null);

  const [formError, setFormError] = useState("");

  /* =======================================================
     SEARCH — client-side
  ======================================================= */

  const [searchText, setSearchText] = useState("");

  /* =======================================================
     PAGINATION (client-side)
  ======================================================= */

  const [page, setPage] = useState(0);

  const [rowsPerPage, setRowsPerPage] = useState(10);

  /* =======================================================
     UI
  ======================================================= */

  const [successOpen, setSuccessOpen] = useState(false);

  const [guideOpen, setGuideOpen] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const showSnackbar = useCallback((message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") return;

    setSnackbar((prev) => ({ ...prev, open: false }));
  };


  /* =======================================================
     FETCH STUDENTS — branch-scoped
     GET /admission/fully-paid-pending-certificate/
     params: organization, branch
  ======================================================= */

  const fetchStudents = useCallback(async () => {
    if (!organizationId || !selectedBranchId) {
      setStudents([]);

      return;
    }

    setLoadingStudents(true);

    try {
      const response = await api.get(STUDENTS_PENDING_APPROVAL_API, {
        params: {
          organization: Number(organizationId),
          branch: Number(selectedBranchId),
        },
      });

      setStudents(getArray(response));
    } catch (error) {
      console.error("Students fetch error:", error);

      setStudents([]);

      showSnackbar(
        getErrorMessage(error, "Failed to load students."),
        "error"
      );
    } finally {
      setLoadingStudents(false);
    }
  }, [organizationId, selectedBranchId, showSnackbar]);

  /* =======================================================
     FETCH CERTIFICATE APPROVALS — branch-scoped
     GET /admission/approval/list/
     params: organization, branch
  ======================================================= */

  const fetchCertificateList = useCallback(async () => {
    if (!organizationId || !selectedBranchId) {
      setSavedCertificates([]);

      setInitialLoading(false);

      return;
    }

    setLoadingList(true);

    try {
      const response = await api.get(APPROVAL_LIST_API, {
        params: {
          organization: Number(organizationId),
          branch: Number(selectedBranchId),
        },
      });

      setSavedCertificates(getArray(response));
    } catch (error) {
      console.error("Approval list error:", error);

      setSavedCertificates([]);

      showSnackbar(
        getErrorMessage(error, "Failed to load certificate list."),
        "error"
      );
    } finally {
      setLoadingList(false);

      setInitialLoading(false);
    }
  }, [organizationId, selectedBranchId, showSnackbar]);

  /* =======================================================
     DASHBOARD COUNTS
     GET /admission/certificate-approval/dashboard-count/
  ======================================================= */

  const fetchDashboardCounts = useCallback(async () => {
    try {
      const response = await api.get(DASHBOARD_COUNT_API);

      setDashboardCounts(response.data);
    } catch (error) {
      console.error("Dashboard count error:", error);

      setDashboardCounts(null);
    }
  }, []);

  /* Re-fetch everything whenever the global branch or
     organization changes (also runs on mount). */

  useEffect(() => {
    if (!organizationId) return;

    handleReset();

    fetchStudents();

    fetchCertificateList();

    fetchDashboardCounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId, selectedBranchId]);

  /* =======================================================
     DERIVED STATS
  ======================================================= */

  const totalApprovals = savedCertificates.length;

  const todayCount = savedCertificates.filter((certificate) =>
    String(certificate.approval_date || "").startsWith(todayStr)
  ).length;

  /* =======================================================
     SEARCH — client-side filter
  ======================================================= */

  const filteredCertificates = useMemo(() => {
    const search = searchText.trim().toLowerCase();

    if (!search) return savedCertificates;

    return savedCertificates.filter((certificate) =>
      [
        certificate.student_name,
        certificate.admission_no,
        certificate.remarks,
        certificate.approved_by,
      ].some((field) =>
        String(field || "").toLowerCase().includes(search)
      )
    );
  }, [savedCertificates, searchText]);

  const displayedCertificates = filteredCertificates.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  /* =======================================================
     HANDLERS
  ======================================================= */

  const handleFormChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));

    setFormError("");
  };

  const handleStudentChange = (studentId) => {
    const student = students.find(
      (item) => String(item.student_id) === String(studentId)
    );

    setSelectedStudent(student || null);

    setFormData((prev) => ({ ...prev, student: studentId }));

    setFormError("");
  };

  const handleReset = () => {
    setFormData(emptyForm);

    setSelectedStudent(null);

    setFormError("");
  };

  const hasPendingFee = Number(selectedStudent?.pending_amount) > 0;

  const handleSave = async () => {
    if (!formData.student) {
      setFormError("Please select a candidate.");

      return;
    }

    if (!formData.approval_date) {
      setFormError("Please select the approval date.");

      return;
    }

    if (!formData.remarks.trim()) {
      setFormError("Please enter the approval remarks.");

      return;
    }

    if (hasPendingFee) {
      setFormError(
        "Fee payment is pending for this candidate. Please clear the fee before approving the certificate."
      );

      return;
    }

    setSaving(true);

    try {
      await api.post(APPROVAL_SAVE_API, {
        admission: Number(formData.student),
        approval_date: formData.approval_date,
        remarks: formData.remarks.trim(),
      });

      showSnackbar("Certificate approval saved successfully.", "success");

      setSuccessOpen(true);

      handleReset();

      fetchCertificateList();

      fetchStudents();

      fetchDashboardCounts();
    } catch (error) {
      console.error("Approval save error:", error);

      setFormError(getErrorMessage(error, "Failed to save the approval."));
    } finally {
      setSaving(false);
    }
  };

  /* =======================================================
     HELPERS
  ======================================================= */

  const menuProps = {
    PaperProps: {
      sx: {
        maxHeight: 260,
        "&::-webkit-scrollbar": { width: "6px" },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: isDark ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.2)",
          borderRadius: "3px",
        },
      },
    },
  };

  const selectStyles = {
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.divider,
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.primary.main,
    },
  };

  const textFieldStyles = {
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.divider,
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.primary.main,
    },
  };

  const headerCellStyle = {
    backgroundColor: theme.palette.primary.main,
    color: "#fff",
    fontWeight: 700,
    whiteSpace: "nowrap",
  };

  const formatDate = (value) =>
    value
      ? new Date(
          String(value).split("-").reverse().join("-")
        ).toLocaleDateString()
      : "—";

  /* =======================================================
     UI
  ======================================================= */

  return (
    <Box
      sx={{
        p: { xs: 2, md: 3 },
        minHeight: "100vh",
        color: theme.palette.text.primary,
      }}
    >
      {/* PAGE HEADER */}

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1.5,
          mb: 3,
        }}
      >
       
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Tooltip title="Reload">
            <span>
              <IconButton
                size="small"
                onClick={() => {
                  fetchStudents();

                  fetchCertificateList();
                }}
                disabled={!organizationId || !selectedBranchId}
                sx={{
                  color: theme.palette.primary.main,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                  borderRadius: 1.5,
                }}
              >
                <Refresh fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title="User guide">
            <IconButton
              size="small"
              onClick={() => setGuideOpen(true)}
              sx={{
                color: theme.palette.primary.main,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                borderRadius: 1.5,
              }}
            >
              <InfoOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* SUMMARY STATS */}

      <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
        <StatCard
          icon={Verified}
          label="Total Approvals"
          value={loadingList ? "…" : totalApprovals}
          color="#1976d2"
        />

        <StatCard
          icon={ReceiptLong}
          label="Approved Today"
          value={loadingList ? "…" : todayCount}
          color="#2e7d32"
        />

        <StatCard
          icon={TrendingUp}
          label="Students Available"
          value={loadingStudents ? "…" : students.length}
          color="#ed6c02"
        />

        {dashboardCounts &&
          Object.entries(dashboardCounts)
            .filter(
              ([key, value]) =>
                typeof value === "number" &&
                !["total", "count"].includes(key)
            )
            .slice(0, 2)
            .map(([key, value]) => (
              <StatCard
                key={key}
                icon={Description}
                label={prettifyFieldLabel(key)}
                value={value}
                color="#7b1fa2"
              />
            ))}
      </Box>

      {/* FORM CARD */}

      <Card
        sx={{
          borderRadius: 3,
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: "none",
          mb: 3,
        }}
      >
        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          {/* INLINE ERROR */}

          {formError && (
            <Alert
              severity={hasPendingFee ? "warning" : "error"}
              icon={hasPendingFee ? <WarningAmber fontSize="inherit" /> : <ErrorOutline fontSize="inherit" />}
              onClose={() => setFormError("")}
              sx={{ mb: 2.5, borderRadius: "8px", fontSize: "0.85rem" }}
            >
              {hasPendingFee && formError.includes("pending") ? (
                <>
                  <Typography sx={{ fontWeight: 700, fontSize: "0.85rem", mb: 0.3 }}>
                    Fee Pending
                  </Typography>
                  {formError}
                </>
              ) : (
                formError
              )}
            </Alert>
          )}

          {/* CANDIDATE DETAILS */}

          <SectionLabel icon={PersonSearch} text="Candidate Details" />

          <FormRow label="Select Candidate" required>
            <FormControl fullWidth size="small">
              <Select
                value={formData.student || ""}
                displayEmpty
                disabled={loadingStudents || !selectedBranchId}
                onChange={(event) => handleStudentChange(event.target.value)}
                MenuProps={menuProps}
                sx={selectStyles}
              >
                <MenuItem value="">
                  <em style={{ color: "#9e9e9e" }}>
                    {loadingStudents
                      ? "Loading students…"
                      : !selectedBranchId
                      ? "Select a branch first"
                      : "— Select Candidate —"}
                  </em>
                </MenuItem>

                {students.map((student) => (
                  <MenuItem key={student.student_id} value={student.student_id}>
                    {student.candidate_name} ({student.admission_no})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </FormRow>

          {/* STUDENT INFO STRIP */}

          {selectedStudent && (
            <Box sx={{ ml: { sm: "160px" }, mb: 2 }}>
              {/* BASIC INFO */}

              <Box
                sx={{
                  p: 2,
                  borderRadius: 1.5,
                  bgcolor: theme.palette.action.selected,
                  border: "1px solid",
                  borderColor: "divider",
                  mb: 2,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    flexWrap: "wrap",
                    mb: 1.5,
                  }}
                >
                  <Avatar
                    sx={{
                      bgcolor: "primary.main",
                      width: 36,
                      height: 36,
                      fontSize: "0.9rem",
                      fontWeight: 700,
                    }}
                  >
                    {String(selectedStudent.candidate_name || "C")[0].toUpperCase()}
                  </Avatar>

                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {selectedStudent.candidate_name}
                  </Typography>

                  <Chip
                    label={`Admission No: ${selectedStudent.admission_no}`}
                    size="small"
                    variant="outlined"
                  />
                </Box>

                <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
                  {selectedStudent.email && (
                    <Typography variant="caption">
                      📧 {selectedStudent.email}
                    </Typography>
                  )}

                  {selectedStudent.mobile_no && (
                    <Typography variant="caption">
                      📞 {selectedStudent.mobile_no}
                    </Typography>
                  )}

                  {selectedStudent.branch && (
                    <Typography variant="caption">
                      📍 {selectedStudent.branch}
                    </Typography>
                  )}
                </Box>
              </Box>

              {/* FEE DETAILS */}

              <Box
                sx={{
                  p: 2,
                  borderRadius: 1.5,
                  bgcolor: theme.palette.background.default,
                  border: "1px solid",
                  borderColor: "divider",
                  mb: 2,
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 700,
                    mb: 1.5,
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  <AttachMoney sx={{ fontSize: 16 }} /> Fee Details
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" display="block">
                      Total Fee
                    </Typography>

                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {formatCurrency(selectedStudent.total_fee)}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" display="block">
                      Total Paid
                    </Typography>

                    <Typography variant="body2" sx={{ fontWeight: 600, color: "#2e7d32" }}>
                      {formatCurrency(selectedStudent.total_paid)}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" display="block">
                      Pending Amount
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: hasPendingFee ? "#ed6c02" : "#2e7d32",
                      }}
                    >
                      {formatCurrency(selectedStudent.pending_amount)}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              {/* COURSES & BATCHES */}

              {selectedStudent.courses?.length > 0 && (
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 1.5,
                    bgcolor: theme.palette.background.default,
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 700,
                      mb: 1.5,
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                    }}
                  >
                    <School sx={{ fontSize: 16 }} /> Enrolled Courses & Batches
                  </Typography>

                  <Grid container spacing={1.5}>
                    {selectedStudent.courses.map((course, index) => (
                      <Grid item xs={12} sm={6} key={index}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            p: 1,
                            borderRadius: 1,
                            bgcolor: theme.palette.action.hover,
                          }}
                        >
                          <School sx={{ fontSize: 14, color: "primary.main" }} />

                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {course.course_name}
                            </Typography>

                            <Typography variant="caption">
                              Batch: {course.batch_name}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}
            </Box>
          )}

          <Divider sx={{ my: 2.5 }} />

          {/* APPROVAL DETAILS */}

          <SectionLabel icon={CalendarToday} text="Approval Details" />

          <FormRow label="Approval Date" required>
            <TextField
              size="small"
              type="date"
              fullWidth
              value={formData.approval_date}
              onChange={(event) =>
                handleFormChange("approval_date", event.target.value)
              }
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: todayStr }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CalendarToday sx={{ fontSize: 15, color: "text.disabled" }} />
                  </InputAdornment>
                ),
              }}
              sx={textFieldStyles}
            />
          </FormRow>

          <FormRow label="Description" required>
            <TextField
              fullWidth
              multiline
              rows={4}
              placeholder="Enter approval remarks or description…"
              value={formData.remarks}
              onChange={(event) =>
                handleFormChange("remarks", event.target.value)
              }
              sx={textFieldStyles}
            />
          </FormRow>

          {/* ACTIONS */}

          <Box
            sx={{
              display: "flex",
              gap: 1.5,
              justifyContent: "flex-end",
              mt: 3,
              pt: 2,
              borderTop: "1px solid",
              borderColor: "divider",
            }}
          >
            <Button
              variant="outlined"
              onClick={handleReset}
              disabled={saving}
              sx={{
                borderRadius: "25px",
                height: 40,
                px: 3,
                textTransform: "none",
                fontWeight: 600,
              }}
            >
              Reset
            </Button>

            <Tooltip
              title={
                !selectedBranchId
                  ? "Select a branch first"
                  : !formData.student
                  ? "Select a candidate"
                  : !formData.approval_date
                  ? "Select approval date"
                  : !formData.remarks.trim()
                  ? "Enter description"
                  : hasPendingFee
                  ? "Fee pending — cannot approve"
                  : "Save approval"
              }
            >
              <span>
                <Button
                  variant="contained"
                  onClick={handleSave}
                  disabled={
                    !selectedBranchId ||
                    !formData.student ||
                    !formData.approval_date ||
                    !formData.remarks.trim() ||
                    hasPendingFee ||
                    saving
                  }
                  startIcon={<Save />}
                  sx={{
                    borderRadius: "25px",
                    height: 40,
                    px: 4,
                    boxShadow: "none",
                    textTransform: "none",
                    fontWeight: 700,
                  }}
                >
                  {saving ? "Saving…" : "Save"}
                </Button>
              </span>
            </Tooltip>
          </Box>
        </CardContent>
      </Card>

      {/* TABLE HEADER + SEARCH */}

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1.5,
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2 }}>
            Certificate Approvals
          </Typography>

          {!loadingList && (
            <Typography variant="caption" color="text.disabled">
              {filteredCertificates.length} record
              {filteredCertificates.length !== 1 ? "s" : ""}
              {searchText ? " matching search" : " total"}
            </Typography>
          )}
        </Box>

        <TextField
          size="small"
          placeholder="Search approvals…"
          value={searchText}
          onChange={(event) => {
            setSearchText(event.target.value);

            setPage(0);
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ fontSize: 17, color: "text.disabled" }} />
              </InputAdornment>
            ),
            endAdornment: searchText && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchText("")}>
                  <Close sx={{ fontSize: 15 }} />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            width: 260,
            "& .MuiOutlinedInput-root": {
              borderRadius: "10px",
              height: 40,
              backgroundColor: isDark ? alpha("#fff", 0.05) : alpha("#000", 0.02),
            },
          }}
        />
      </Box>

      {/* TABLE CARD */}

      <Card
        sx={{
          borderRadius: 3,
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: "none",
        }}
      >
        <CustomTableScrollbar sx={{ maxHeight: 560 }}>
          <Table stickyHeader size="small" sx={{ minWidth: 800 }}>
            <TableHead>
              <TableRow>
                {[
                  "S.No",
                  "Admission No.",
                  "Candidate / Client",
                  "Approved Date",
                  "Approved By",
                  "Remark",
                ].map((header) => (
                  <TableCell key={header} sx={headerCellStyle}>
                    {header}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {initialLoading || loadingList ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : displayedCertificates.length > 0 ? (
                displayedCertificates.map((certificate, index) => (
                  <TableRow key={certificate.id} hover>
                    <TableCell sx={{ py: 1.2 }}>
                      {page * rowsPerPage + index + 1}
                    </TableCell>
                    <TableCell sx={{ py: 1.2 }}>
                      <Typography
                        sx={{
                          fontFamily: "monospace",
                          fontSize: "0.82rem",
                          bgcolor: "action.hover",
                          px: 0.8,
                          py: 0.3,
                          borderRadius: 0.8,
                          display: "inline",
                        }}
                      >
                        {certificate.admission_no || "—"}
                      </Typography>
                    </TableCell>

                    <TableCell sx={{ py: 1.2 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Avatar
                          sx={{
                            width: 28,
                            height: 28,
                            bgcolor: "primary.main",
                            fontSize: "0.72rem",
                            fontWeight: 700,
                          }}
                        >
                          {String(certificate.student_name || "C")[0].toUpperCase()}
                        </Avatar>

                        <Typography sx={{ fontSize: "0.875rem", fontWeight: 500 }}>
                          {certificate.student_name || "—"}
                        </Typography>
                      </Box>
                    </TableCell>

                    <TableCell
                      sx={{ py: 1.2, fontSize: "0.82rem", whiteSpace: "nowrap" }}
                    >
                      {formatDate(certificate.approval_date)}
                    </TableCell>

                    <TableCell sx={{ py: 1.2 }}>
                      {certificate.approved_by ? (
                        <Chip
                          label={certificate.approved_by}
                          size="small"
                          color="success"
                          variant="outlined"
                          sx={{ fontWeight: 600, fontSize: "0.72rem", height: 22 }}
                        />
                      ) : (
                        "—"
                      )}
                    </TableCell>

                    <TableCell sx={{ py: 1.2, maxWidth: 260 }}>
                      <Typography
                        sx={{
                          fontSize: "0.82rem",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {certificate.remarks || "—"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                    <Verified
                      sx={{ fontSize: 40, color: "text.disabled", mb: 1, display: "block", mx: "auto" }}
                    />

                    <Typography color="text.disabled" variant="body2">
                      {searchText
                        ? "No approvals match your search"
                        : selectedBranchId
                        ? "No certificate approvals found"
                        : "Select a branch to load approvals"}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CustomTableScrollbar>

        <Divider />

        {/* PAGINATION — inside the Card */}

        <TablePagination
          component="div"
          count={filteredCertificates.length}
          page={page}
          onPageChange={(event, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(Number(event.target.value));

            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Card>

      {/* SUCCESS DIALOG */}

      <Dialog open={successOpen} onClose={() => setSuccessOpen(false)} maxWidth="xs" fullWidth>
        <DialogContent sx={{ p: 4, textAlign: "center" }}>
          <CheckCircle sx={{ fontSize: 54, color: "success.main", mb: 1.5 }} />

          <Typography variant="h6" fontWeight={700} gutterBottom>
            Certificate Saved
          </Typography>

          <Typography color="text.disabled" sx={{ mb: 3, fontSize: "0.9rem" }}>
            Certificate approval has been saved successfully.
          </Typography>

          <Button
            variant="contained"
            onClick={() => setSuccessOpen(false)}
            sx={{
              borderRadius: "25px",
              minWidth: 120,
              boxShadow: "none",
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            Done
          </Button>
        </DialogContent>
      </Dialog>

      {/* USER GUIDE DIALOG */}

      <Dialog open={guideOpen} onClose={() => setGuideOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{
            backgroundColor: theme.palette.primary.main,
            color: "#fff",
            fontWeight: 700,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          Certificate Approval — User Guide

          <IconButton onClick={() => setGuideOpen(false)} sx={{ color: "#fff" }}>
            <Close />
          </IconButton>
        </DialogTitle>

        <StyledDialogContent dividers sx={{ p: 3 }}>
          {[
            [
              "Select Candidate",
              "Choose a fully paid candidate from the dropdown. The candidate's details, fee information, and enrolled courses will be displayed automatically.",
            ],
            [
              "Fee Details",
              "Review the total fee, paid amount, and pending amount. If there is a pending amount, a warning will appear and the Save button will be disabled.",
            ],
            [
              "Approval Date",
              "Pick the date on which the certificate is being approved.",
            ],
            [
              "Description",
              "Enter the approval remarks or reason for certificate approval.",
            ],
            [
              "Save",
              "Click Save to record the certificate approval. The form will reset and the table will refresh. Save is only enabled if the candidate has no pending fee.",
            ],
            [
              "Browse Records",
              "Use the search box to filter records. Navigate pages using the pagination at the bottom.",
            ],
          ].map(([title, desc], index) => (
            <Box key={title} sx={{ display: "flex", gap: 2, mb: 2.5 }}>
              <Box
                sx={{
                  width: 26,
                  height: 26,
                  borderRadius: "50%",
                  flexShrink: 0,
                  bgcolor: "primary.main",
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "0.78rem",
                  fontWeight: 700,
                  mt: 0.2,
                }}
              >
                {index + 1}
              </Box>

              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: "0.9rem", mb: 0.3 }}>
                  {title}
                </Typography>

                <Typography variant="body2" color="text.disabled" sx={{ lineHeight: 1.5 }}>
                  {desc}
                </Typography>
              </Box>
            </Box>
          ))}
        </StyledDialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            variant="contained"
            onClick={() => setGuideOpen(false)}
            sx={{ borderRadius: "25px", boxShadow: "none", textTransform: "none" }}
          >
            Got it
          </Button>
        </DialogActions>
      </Dialog>

      {/* SNACKBAR — bottom right */}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default CertificateApproval;